import { Turnstile } from "@marsidev/react-turnstile";
import type { FormEvent, JSX } from "react";
import { useEffect, useRef, useState } from "react";

import type { CityContactFailure } from "@/lib/api";
import { submitCityContact } from "@/lib/api";

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

type Status = "idle" | "sending" | "sent" | "error";

const FAILURE_COPY: Record<CityContactFailure, string> = {
  captcha: "Please complete the check again.",
  invalid: "Please check the form and try again.",
  throttled: "Too many messages from here. Try again in a few minutes.",
  unavailable: "That did not go through. Please try again.",
  unknown: "That did not go through. Please try again.",
};

const inputClass =
  "w-full border border-white/15 bg-white/5 px-4 py-3 font-mono text-sm text-paper placeholder:text-paper-muted/50 focus:border-accent focus:outline-none";
const labelClass =
  "mb-2 block font-mono text-[0.65rem] font-semibold tracking-wider text-paper-muted";

interface ContactFormProps {
  slug: string;
  cityName: string;
}

/**
 * The contact form on a public guide page.
 *
 * The document sends the city slug and nothing else that names a recipient: the
 * API decides which inbox the message lands in, so no address is rendered here,
 * sent in a request body, or guessable from this page. Submitting is answered by
 * the API with a reason rather than an error, because a failed check, a rejected
 * field and a rate limit each need different words.
 */
export function ContactForm({ slug, cityName }: ContactFormProps): JSX.Element {
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(undefined);
  const [captchaUnavailable, setCaptchaUnavailable] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);
  // The widget mounts after hydration, so the baked document carries none of the
  // Turnstile library's own markup and hydration has nothing to disagree about.
  const [mounted, setMounted] = useState(false);
  const startedAt = useRef<number | null>(null);
  const sentRef = useRef<HTMLOutputElement>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Both notices carry their own semantics (an `output` is a status, the error is
  // an alert), so focus follows whichever one replaced the form.
  useEffect(() => {
    if (status === "sent") sentRef.current?.focus();
    else if (status === "error") errorRef.current?.focus();
  }, [status]);

  // First keystroke to submit: the way a bot and a reader are told apart. It
  // hangs off the fields themselves, which are the interactive elements.
  function markStart(): void {
    startedAt.current ??= Date.now();
  }

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    // Without a token the button is disabled; a stray Enter does nothing.
    if (SITE_KEY && !turnstileToken) return;

    const form = new FormData(event.currentTarget);
    setStatus("sending");
    setError(null);

    const phone = String(form.get("phone") ?? "").trim();
    const result = await submitCityContact({
      slug,
      name: String(form.get("name") ?? ""),
      email: String(form.get("email") ?? ""),
      ...(phone ? { phone } : {}),
      message: String(form.get("message") ?? ""),
      // The trap travels empty for a person; a filled one is dropped server-side.
      website: String(form.get("website") ?? ""),
      ...(turnstileToken ? { turnstileToken } : {}),
      ...(startedAt.current === null ? {} : { elapsedMs: Date.now() - startedAt.current }),
      locale: navigator.language.slice(0, 10),
    });

    if (result.ok) {
      setStatus("sent");
      return;
    }

    setStatus("error");
    setError(FAILURE_COPY[result.reason]);
    // A Turnstile token is single use, so the next attempt needs a fresh widget.
    setTurnstileToken(undefined);
    setCaptchaKey((key) => key + 1);
  }

  if (status === "sent") {
    return (
      <output ref={sentRef} tabIndex={-1} className="atlas-guide-prose block">
        Thanks. Your message is with the {cityName} team.
      </output>
    );
  }

  return (
    <form
      onSubmit={(event) => void submit(event)}
      className="atlas-guide-contact-form grid gap-5 pt-2 sm:grid-cols-2"
      data-contact-slug={slug}
    >
      <div>
        <label htmlFor="contact-name" className={labelClass}>
          NAME
        </label>
        <input
          id="contact-name"
          name="name"
          required
          maxLength={120}
          autoComplete="name"
          onFocus={markStart}
          className={inputClass}
        />
      </div>
      <div>
        <label htmlFor="contact-email" className={labelClass}>
          EMAIL
        </label>
        <input
          id="contact-email"
          name="email"
          type="email"
          required
          maxLength={254}
          autoComplete="email"
          onFocus={markStart}
          className={inputClass}
        />
      </div>
      <div className="sm:col-span-2">
        <label htmlFor="contact-phone" className={labelClass}>
          PHONE (optional)
        </label>
        <input
          id="contact-phone"
          name="phone"
          type="tel"
          maxLength={40}
          autoComplete="tel"
          onFocus={markStart}
          className={inputClass}
        />
      </div>
      <div className="sm:col-span-2">
        <label htmlFor="contact-message" className={labelClass}>
          MESSAGE
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={5}
          minLength={10}
          maxLength={4000}
          onFocus={markStart}
          className={inputClass}
        />
      </div>

      {/* Honeypot: off-screen rather than display:none, unhidden from the DOM, and
          the one field a person is never asked to fill in. */}
      <div className="atlas-contact-trap" aria-hidden="true">
        <label htmlFor="contact-website">Website</label>
        <input id="contact-website" name="website" tabIndex={-1} autoComplete="off" />
      </div>

      {SITE_KEY && mounted && (
        <div className="sm:col-span-2">
          <Turnstile
            key={captchaKey}
            siteKey={SITE_KEY}
            options={{ theme: "dark" }}
            onSuccess={setTurnstileToken}
            onExpire={() => setTurnstileToken(undefined)}
            onError={() => {
              setTurnstileToken(undefined);
              setCaptchaUnavailable(true);
            }}
          />
        </div>
      )}

      {captchaUnavailable && (
        <p className="sm:col-span-2 font-mono text-[0.65rem] tracking-wider text-accent">
          The check did not load. A blocker may be stopping Cloudflare.{" "}
          <button
            type="button"
            onClick={() => {
              setCaptchaUnavailable(false);
              setCaptchaKey((key) => key + 1);
            }}
            className="underline hover:text-paper"
          >
            Retry
          </button>
        </p>
      )}

      {status === "error" && error && (
        <p
          ref={errorRef}
          tabIndex={-1}
          role="alert"
          className="sm:col-span-2 font-mono text-[0.65rem] tracking-wider text-accent"
        >
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-5 sm:col-span-2">
        <button
          type="submit"
          disabled={status === "sending" || (SITE_KEY ? !turnstileToken : false)}
          className="bg-accent px-6 py-3 font-anton text-lg uppercase tracking-wide text-ink transition-opacity disabled:opacity-40"
        >
          {status === "sending" ? "SENDING…" : "SEND MESSAGE"}
        </button>
      </div>
    </form>
  );
}
